import { useState, useEffect, useCallback, useRef } from "react"
import { toast } from "sonner"
import { CompanySettings } from "@/types"
import { CompanyFormValues } from "./companySettingsSchema"

import { useAuth } from "@/contexts/auth"
import { getFormDefaultValues } from "./useCompanySettingsUtils"
import { supabase } from "@/integrations/supabase/client"

export function useCompanySettings() {
  const [isLoading, setIsLoading] = useState(false)
  const [settings, setSettings] = useState<CompanySettings | null>(null)
  const [, setError] = useState<string | null>(null)
  const [companyId, setCompanyId] = useState<string | null>(null)
  const { user } = useAuth()
  const hasLoadedSettings = useRef(false)
  
  const fetchCompanySettings = useCallback(async () => {
    // Skip if we've already loaded settings to prevent unnecessary API calls
    if (hasLoadedSettings.current && settings) return;
    
    try {
      setIsLoading(true)
      setError(null)
      if (!user?.companyId) {
        throw new Error("User does not have a companyId")
      }
      
      let userCompanyId = user?.companyId;
      
      if (!userCompanyId) {
        if (!user || !user.id) {
          setError("No user ID found");
          toast.error("Unable to load user information");
          setIsLoading(false);
          return;
        }
        
        try {
          const { data: profileData, error: profileError } = await supabase
            .from('profiles')
            .select('company_id')
            .eq('id', user.id)
            .single();
            
          if (profileError) {
            setError("Failed to load user profile");
            toast.error("Unable to load user information");
            setIsLoading(false);
            return;
          }
          
          userCompanyId = profileData.company_id;
        } catch (err: unknown) {
          const errMsg = err instanceof Error ? err.message : "Failed to load user profile";
          setError(errMsg);
          toast.error(errMsg);
          setIsLoading(false);
          return;
        }
      }
      
      if (!userCompanyId) {
        setError("No company ID found for the current user");
        toast.error("Unable to load company settings. No company assigned to your account.");
        setIsLoading(false);
        return;
      }
      
      setCompanyId(userCompanyId);
      
      try {
        const { data: companyData, error: companyError } = await supabase
          .from('companies')
          .select('name, id, max_overtime_hours, week_start_day')
          .eq('id', userCompanyId)
          .single();
        
        if (companyError) {
          setError(`Failed to load company information: ${companyError.message}`);
          toast.error("Failed to load company information");
          setIsLoading(false);
          return;
        }
        
        if (!companyData) {
          setError(`No company found with ID ${userCompanyId}`);
          toast.error("Company not found");
          setIsLoading(false);
          return;
        }
        
        const settingsData: CompanySettings = {
          id: companyData.id,
          company_name: companyData.name,
          companyName: companyData.name,
          max_overtime_hours: companyData.max_overtime_hours,
          maxOvertimeHours: companyData.max_overtime_hours,
          week_start_day: companyData.week_start_day,
          weekStartDay: companyData.week_start_day
        };
        
        setSettings(settingsData);
        hasLoadedSettings.current = true;
        
      } catch (err: unknown) {
        const errMsg = err instanceof Error ? err.message : "Failed to load company information";
        setError(errMsg);
        toast.error(errMsg);
      } finally {
        setIsLoading(false);
      }
    } catch (err: unknown) {
      const errMsg = err instanceof Error ? err.message : "Failed to load company settings";
      setError(errMsg);
      toast.error("An error occurred while loading settings");
      setIsLoading(false);
    }
  }, [user, settings]);
  
  // Fetch company settings when the component mounts
  useEffect(() => {
    if (!hasLoadedSettings.current) {
      fetchCompanySettings();
    }
  }, [fetchCompanySettings]); // Re-fetch when fetchCompanySettings changes

  const handleSaveSettings = useCallback(async (data: CompanyFormValues) => {
    try {
      setIsLoading(true)
      
      if (!companyId) {
        setError("Cannot save settings: No company ID")
        toast.error("Failed to save: No company ID found")
        setIsLoading(false)
        return
      }
      
      const overtimeHours = typeof data.maxOvertimeHours === 'string' 
        ? parseFloat(data.maxOvertimeHours) 
        : data.maxOvertimeHours;
      
      const { error: updateError } = await supabase
        .from('companies')
        .update({ 
          name: data.companyName,
          max_overtime_hours: overtimeHours,
          week_start_day: data.weekStartDay
        })
        .eq('id', companyId);
      
      if (updateError) {
        setError(`Failed to save settings: ${updateError.message}`);
        toast.error("Failed to save company settings");
        setIsLoading(false);
        return;
      }
      
      if (user && user.id) {
        try {
          await supabase
            .from('profiles')
            .update({ 
              name: data.companyName,
              updated_at: new Date().toISOString()
            })
            .eq('id', user.id);
        } catch {
          // intentionally empty
        }
      }
      
      // Update local state to match new saved data
      setSettings(prevSettings => {
        if (!prevSettings) return null;
        return {
          ...prevSettings,
          companyName: data.companyName,
          company_name: data.companyName,
          maxOvertimeHours: overtimeHours,
          max_overtime_hours: overtimeHours,
          weekStartDay: data.weekStartDay,
          week_start_day: data.weekStartDay
        };
      });
      
      toast.success("Company settings saved successfully");
      
    } catch {
      toast.error("An error occurred while saving settings");
    } finally {
      setIsLoading(false);
    }
  }, [companyId, user, setSettings]);

  // Create a stable version of getFormDefaultValues that doesn't change on re-renders
  const getStableDefaultValues = useCallback(() => {
    return getFormDefaultValues(settings);
  }, [settings]);

  return {
    isLoading,
    settings,
    getFormDefaultValues: getStableDefaultValues,
    saveSettings: handleSaveSettings
  }
}
